<template>
  <main class="industry-detail-page" :aria-busy="detailStatus === 'LOADING'">
    <template v-if="detailStatus === 'LOADING'">
      <section class="industry-detail-hero industry-detail-hero--loading" role="status" aria-live="polite">
        <p>{{ t('industryDetail.loading') }}</p>
      </section>
      <div class="industry-detail-content" aria-hidden="true">
        <div class="industry-detail-container industry-detail-loading-lines">
          <span v-for="line in 4" :key="line"></span>
        </div>
      </div>
    </template>

    <template v-else-if="detailStatus === 'SUCCESS' && detail">
      <section class="industry-detail-hero" :style="heroStyle" aria-labelledby="detail-hero-title">
        <div class="industry-detail-hero__content">
          <h1 id="detail-hero-title">{{ detail.title }}</h1>
          <p>{{ detail.description }}</p>
        </div>
      </section>

      <article class="industry-detail-content">
        <div class="industry-detail-container">
          <div v-if="richTextContent" class="industry-detail-richtext ql-editor" v-html="richTextContent"></div>

          <section v-if="recommendedProducts.length" class="product-recommendations" aria-labelledby="recommendations-title">
            <h2 id="recommendations-title">{{ t('industryDetail.recommendations') }}</h2>
            <div class="product-recommendations__grid">
              <component
                :is="recommendedProductLink(product) ? 'a' : 'article'"
                v-for="product in recommendedProducts"
                :key="product.model"
                class="product-recommendation"
                :class="{ 'product-recommendation--interactive': recommendedProductLink(product) }"
                v-bind="recommendedProductCardProps(product)"
                @click="openRecommendedProduct($event, product)"
              >
                <div class="product-recommendation__visual">
                  <img :src="product.mainImageUrl" :alt="`${product.model} body composition analyzer`" loading="lazy" decoding="async">
                </div>
                <h3>{{ product.model }}</h3>
              </component>
            </div>
          </section>
        </div>
      </article>
    </template>

    <section v-else-if="detailStatus === 'NOT_FOUND'" class="industry-detail-not-found" aria-labelledby="industry-detail-not-found-title">
      <h1 id="industry-detail-not-found-title">{{ t('industryDetail.notFound') }}</h1>
      <router-link :to="`/industries/${$route.params.categoryId}`">{{ t('industryDetail.returnToIndustry') }}</router-link>
    </section>

    <section v-else-if="detailStatus === 'ERROR'" class="industry-detail-error">
      <h1 role="alert">{{ t('industryDetail.loadError') }}</h1>
      <button type="button" @click="retryDetail">{{ t('industryDetail.retry') }}</button>
      <router-link :to="`/industries/${$route.params.categoryId}`">{{ t('industryDetail.returnToIndustry') }}</router-link>
    </section>
  </main>
</template>

<script>
import { solutionAPI } from '@/utils/api'

const {
  normalizeIndustryCategoriesResponse,
  normalizeIndustryDetailResponse,
  resolveIndustryLanguagePath,
  getRecommendedProductLinkUrl,
  sanitizeIndustryRichText
} = require('@/utils/industries-data.cjs')
const { siteLanguageMixin } = require('@/utils/site-language.js')
const { createLatestSiteRequestGuard } = require('@/utils/site-request.cjs')

export default {
  name: 'IndustryDetail',
  mixins: [siteLanguageMixin],
  data() {
    return {
      detail: null,
      detailStatus: 'LOADING',
      retryLanguageRedirect: false,
      detailRequestGuard: createLatestSiteRequestGuard()
    }
  },
  computed: {
    recommendedProducts() {
      const products = this.detail && this.detail.recommendedProducts
      return Array.isArray(products) ? products.filter(product => product
        && typeof product.model === 'string' && product.model.trim()
        && typeof product.mainImageUrl === 'string' && product.mainImageUrl.trim()) : []
    },
    heroStyle() {
      return this.detail ? { backgroundImage: `linear-gradient(rgba(239, 10, 106, 0.58), rgba(159, 4, 74, 0.56)), url(${this.detail.bannerUrl})` } : {}
    },
    richTextContent() {
      return sanitizeIndustryRichText(this.detail && this.detail.content)
    }
  },
  watch: {
    '$route.fullPath'() {
      return this.fetchDetail()
    },
    currentLanguage() {
      return this.redirectToCurrentLanguageIndustry()
    }
  },
  async mounted() {
    await this.fetchDetail()
  },
  beforeUnmount() {
    this.detailRequestGuard.start(this.currentLanguage)
  },
  methods: {
    isDetailNotFound(response) {
      return response?.response?.status === 404 || response?.status === 404
        || (response?.code === 1 && ['行业专题不存在', '行业类别不存在'].includes(response.message))
    },
    retryDetail() {
      return this.retryLanguageRedirect ? this.redirectToCurrentLanguageIndustry() : this.fetchDetail()
    },
    recommendedProductLink(product) {
      return getRecommendedProductLinkUrl(product && product.linkUrl)
    },
    recommendedProductCardProps(product) {
      const linkUrl = this.recommendedProductLink(product)
      return linkUrl ? { href: linkUrl, target: '_blank', rel: 'noopener' } : {}
    },
    openRecommendedProduct(event, product) {
      const linkUrl = this.recommendedProductLink(product)
      if (!linkUrl) return
      event.preventDefault()
      window.open(linkUrl, '_blank', 'noopener')
    },
    async fetchDetail() {
      const request = this.detailRequestGuard.start(this.currentLanguage)
      this.detailStatus = 'LOADING'
      this.retryLanguageRedirect = false
      this.detail = null
      const { categoryId, topicId } = this.$route.params
      if (!categoryId || !topicId) {
        this.detailStatus = 'NOT_FOUND'
        return
      }
      try {
        const response = await solutionAPI.getTopicDetail(categoryId, topicId, request.siteType)
        if (!this.detailRequestGuard.isCurrent(request, this.currentLanguage)) return
        if (this.isDetailNotFound(response)) {
          this.detailStatus = 'NOT_FOUND'
          return
        }
        this.detail = normalizeIndustryDetailResponse(response)
        this.detailStatus = 'SUCCESS'
      } catch (error) {
        if (!this.detailRequestGuard.isCurrent(request, this.currentLanguage)) return
        this.detailStatus = this.isDetailNotFound(error) ? 'NOT_FOUND' : 'ERROR'
      }
    },
    async redirectToCurrentLanguageIndustry() {
      const request = this.detailRequestGuard.start(this.currentLanguage)
      this.detailStatus = 'LOADING'
      this.retryLanguageRedirect = true
      this.detail = null
      try {
        const categories = normalizeIndustryCategoriesResponse(await solutionAPI.getCategories(request.siteType))
        if (!this.detailRequestGuard.isCurrent(request, this.currentLanguage)) return
        await this.$router.replace(resolveIndustryLanguagePath(categories))
      } catch (error) {
        if (!this.detailRequestGuard.isCurrent(request, this.currentLanguage)) return
        this.detailStatus = 'ERROR'
      }
    }
  }
}
</script>

<style scoped>
.industry-detail-page {
  min-height: 100vh;
  padding-top: var(--global-header-height, 44px);
  overflow-x: clip;
  color: #000;
  background: #fff;
  font-family: MiSans, Arial, sans-serif;
}

.industry-detail-container {
  width: min(1296px, calc(100vw - 144px));
  margin-left: max(72px, calc(50vw - 648px));
}

.industry-detail-hero {
  width: min(1296px, calc(100vw - 144px));
  display: flex;
  align-items: center;
  justify-content: center;
  height: 397px;
  margin-top: 60px;
  margin-left: max(72px, calc(50vw - 648px));
  background-color: #9f044a;
  background-position: center;
  background-size: cover;
}

.industry-detail-hero__content {
  width: min(calc(100% - 48px), 1120px);
  color: #fff;
  text-align: center;
}

.industry-detail-hero h1 {
  margin: 0;
  color: #fff;
  font-size: 58px;
  font-weight: 700;
  line-height: 1.22;
  text-transform: uppercase;
}

.industry-detail-hero p {
  max-width: 1040px;
  margin: 25px auto 0;
  color: #fff;
  font-size: 20px;
  line-height: 1.4;
}

.industry-detail-content {
  padding: 80px 0 120px;
}

.industry-detail-content h2 {
  margin: 0;
  color: #000;
  font-size: 36px;
  font-weight: 700;
  line-height: 1.3;
}

.industry-detail-lead,
.detail-copy p {
  margin: 36px 0 0;
  color: #000;
  font-size: 16px;
  line-height: 1.4;
  text-align: left;
}

.industry-detail-richtext.ql-editor {
  min-height: 0;
  margin-top: 36px;
  padding: 0;
  overflow: visible;
  border: 0;
  color: #000;
  background: transparent;
  font: inherit;
  font-size: 16px;
  line-height: 1.5;
  text-align: left;
  overflow-wrap: anywhere;
}

.industry-detail-richtext :deep(p),
.industry-detail-richtext :deep(ul),
.industry-detail-richtext :deep(ol),
.industry-detail-richtext :deep(blockquote),
.industry-detail-richtext :deep(pre),
.industry-detail-richtext :deep(table) {
  margin: 0 0 24px;
}

.industry-detail-richtext :deep(p) {
  color: inherit;
}

.industry-detail-richtext :deep(h1),
.industry-detail-richtext :deep(h2),
.industry-detail-richtext :deep(h3),
.industry-detail-richtext :deep(h4),
.industry-detail-richtext :deep(h5),
.industry-detail-richtext :deep(h6) {
  margin: 32px 0 16px;
  color: #000;
  font-weight: 700;
  line-height: 1.3;
}

.industry-detail-richtext :deep(img),
.industry-detail-richtext :deep(video),
.industry-detail-richtext :deep(iframe) {
  display: block;
  width: auto !important;
  max-width: 100%;
  height: auto !important;
  margin: 30px 0;
  border: 0;
  border-radius: 24px;
}

.industry-detail-richtext :deep(video),
.industry-detail-richtext :deep(iframe) {
  aspect-ratio: 16 / 9;
}

.industry-detail-richtext :deep(ul),
.industry-detail-richtext :deep(ol) { padding-left: 24px; }
.industry-detail-richtext :deep(a) { color: #ef0a6a; }
.industry-detail-richtext :deep(*:last-child) { margin-bottom: 0; }

.detail-copy {
  margin-top: 36px;
}

.detail-copy h3 {
  margin: 36px 0 0;
  color: #000;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.4;
}

.detail-copy h3 + p {
  margin-top: 0;
}

.detail-copy p {
  margin-top: 0;
}

.detail-image {
  margin: 36px 0 0;
  overflow: hidden;
  border-radius: 24px;
  background: #f5f5f7;
}

.detail-image--large {
  height: 675px;
  margin-top: 24px;
}

.detail-image img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.detail-image-pair {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0;
  margin-top: 36px;
}

.detail-image-pair .detail-image {
  height: 337px;
  margin: 0;
  border-radius: 0;
}

.detail-image-pair .detail-image:first-child {
  border-radius: 24px 0 0 24px;
}

.detail-image-pair .detail-image:last-child {
  border-radius: 0 24px 24px 0;
}

.product-recommendations {
  margin-top: 110px;
}

.product-recommendations h2 {
  font-size: 40px;
}

.product-recommendations__grid {
  display: grid;
  grid-template-columns: repeat(5, 240px);
  gap: 24px;
  margin-top: 44px;
}

.product-recommendation {
  min-width: 0;
  height: 268px;
  text-align: center;
}

.product-recommendation--interactive {
  cursor: pointer;
}

.product-recommendation__visual {
  display: flex;
  width: 100%;
  height: 240px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 12px;
  background: #f5f5f7;
}

.product-recommendation__visual img {
  display: block;
  width: 86%;
  height: 86%;
  object-fit: contain;
}

.product-recommendation h3 {
  margin: 10px 0 0;
  color: #000;
  font-size: 18px;
  font-weight: 700;
  line-height: 18px;
}

.industry-detail-hero--loading {
  background-color: #f5f5f7;
}

.industry-detail-hero--loading p {
  margin: 0;
  color: #6e6e73;
  font-size: 16px;
}

.industry-detail-loading-lines {
  display: grid;
  gap: 24px;
}

.industry-detail-loading-lines span {
  height: 16px;
  background: #f5f5f7;
  border-radius: 4px;
}

.industry-detail-loading-lines span:last-child {
  width: 65%;
}

.industry-detail-not-found,
.industry-detail-error {
  display: flex;
  min-height: calc(100vh - var(--global-header-height, 44px));
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 18px;
  padding: 32px 16px;
  text-align: center;
}

.industry-detail-not-found h1,
.industry-detail-error h1 {
  margin: 0;
  color: #000;
  font-size: 32px;
}

.industry-detail-not-found a,
.industry-detail-error a {
  color: #ef0a6a;
}

.industry-detail-error button {
  padding: 10px 20px;
  border: 0;
  border-radius: 6px;
  background: #ef0a6a;
  color: #fff;
  font: inherit;
  cursor: pointer;
}

@media (max-width: 900px) {
  .industry-detail-container,
  .industry-detail-hero {
    width: calc(100vw - 48px);
    margin-left: 24px;
  }

  .product-recommendations__grid {
    gap: 16px;
  }
}

@media (min-width: 701px) and (max-width: 1379px) {
  .product-recommendations__grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 700px) {
  .industry-detail-page {
    padding-top: var(--global-header-height, 50px);
  }

  .industry-detail-container,
  .industry-detail-hero {
    width: min(400px, calc(100vw - 32px));
    margin-left: 16px;
  }

  .industry-detail-hero {
    height: 240px;
    margin-top: 0;
  }

  .industry-detail-hero__content {
    width: calc(100% - 32px);
  }

  .industry-detail-hero h1 {
    font-size: 30px;
    line-height: 1.2;
  }

  .industry-detail-hero p {
    margin-top: 12px;
    font-size: 14px;
  }

  .industry-detail-content {
    padding: 40px 0 72px;
  }

  .industry-detail-content > .industry-detail-container > h2 {
    font-size: 36px;
  }

  .product-recommendations h2 {
    font-size: 30px;
  }

  .industry-detail-lead,
  .industry-detail-richtext,
  .detail-copy p,
  .detail-copy h3 {
    font-size: 14px;
    line-height: 1.5;
  }

  .industry-detail-lead,
  .industry-detail-richtext,
  .detail-copy {
    margin-top: 28px;
  }

  .detail-copy h3 {
    margin-top: 28px;
  }

  .detail-image--large {
    height: auto;
    aspect-ratio: 1296 / 675;
    margin-top: 28px;
  }

  .detail-image-pair {
    grid-template-columns: 1fr;
    gap: 16px;
    margin-top: 28px;
  }

  .detail-image-pair .detail-image {
    height: auto;
    aspect-ratio: 648 / 337;
    border-radius: 16px;
  }

  .product-recommendations {
    margin-top: 72px;
  }

  .product-recommendations__grid {
    display: flex;
    gap: 24px;
    margin-top: 32px;
    padding-bottom: 4px;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
  }

  .product-recommendations__grid::-webkit-scrollbar {
    display: none;
  }

  .product-recommendation {
    height: 268px;
    flex: 0 0 240px;
  }

  .product-recommendation__visual {
    width: 240px;
    height: 240px;
  }
}
</style>
